<script setup lang="ts">
/**
 * Lessons the unattended charge could not pay for, paid here with the student
 * present. A bank that wants the cardholder can only be satisfied this way.
 */
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card'
import { Button } from '@/Components/ui/button'
import { AlertCircle } from 'lucide-vue-next'
import CardPicker from '@/Components/Payment/CardPicker.vue'
import { chargeLessonsTo } from '@/lib/savedCards'
import { loadStripe } from '@/lib/stripe'

interface UnpaidLesson {
  id: number
  tutor_name: string
  starts_at: string
  amount: string
  reason: string | null
}

const props = defineProps<{
  lessons: UnpaidLesson[]
  stripeKey: string
}>()

const emit = defineEmits<{
  changed: []
}>()

const picker = ref<InstanceType<typeof CardPicker> | null>(null)
const pickerVersion = ref(0)
const payingLessonId = ref<number | null>(null)
const errors = ref<Record<number, string>>({})

async function pay(lesson: UnpaidLesson) {
  payingLessonId.value = lesson.id
  errors.value[lesson.id] = ''

  try {
    const { paymentMethodId, remember } = await picker.value!.choose()

    const { data } = await window.axios.post(`/student/settings/billing/lessons/${lesson.id}/pay`, {
      payment_method_id: paymentMethodId,
    })

    if (data.client_secret) {
      const stripe = await loadStripe(props.stripeKey)
      const result = await stripe.handleNextAction({ clientSecret: data.client_secret })

      if (result.error) {
        errors.value[lesson.id] = result.error.message
        return
      }

      await window.axios.post(`/student/settings/billing/lessons/${lesson.id}/confirm`, {
        payment_intent_id: result.paymentIntent.id,
      })
    }

    if (remember) {
      await chargeLessonsTo(paymentMethodId)
    }

    emit('changed')
    pickerVersion.value++
    router.reload()
  } catch (error: any) {
    errors.value[lesson.id] = error.response?.data?.message ?? error.message ?? 'The payment did not go through. Please try again.'
  } finally {
    payingLessonId.value = null
  }
}
</script>

<template>
  <Card v-if="lessons.length > 0" class="border-red-200">
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <AlertCircle class="w-5 h-5 text-red-600" />
        Lessons waiting for payment
      </CardTitle>
      <CardDescription>
        We could not take payment for these. They go ahead as booked, but your tutor is only paid once you pay, so please pay as soon as you can.
      </CardDescription>
    </CardHeader>
    <CardContent class="space-y-4">
      <CardPicker :key="pickerVersion" ref="picker" :stripe-key="stripeKey" />

      <div v-for="lesson in lessons" :key="lesson.id" class="rounded-xl border border-gray-200 p-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="font-medium text-gray-900">{{ lesson.tutor_name }} · {{ lesson.starts_at }}</p>
          </div>
          <Button :disabled="!picker?.isReady || payingLessonId !== null" @click="pay(lesson)">
            {{ payingLessonId === lesson.id ? 'Paying…' : `Pay £${lesson.amount}` }}
          </Button>
        </div>
        <p v-if="lesson.reason" class="mt-2 text-sm text-gray-600">{{ lesson.reason }}</p>
        <p v-if="errors[lesson.id]" role="alert" class="mt-2 text-sm text-red-600">{{ errors[lesson.id] }}</p>
      </div>
    </CardContent>
  </Card>
</template>
